import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Checkbox, Drawer, IconButton, InputAdornment, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateCategoryAssigneesMutation } from 'api/mutations/useUpdateCategoryAssigneesMutation';
import { useUsersListQuery } from 'api/queries/useUsersListQuery';
import { CategoryAssignee } from 'api/types';
import { alert, NewConfirmDialog } from 'components';
import { useWindowSize } from 'hooks/useWindowSize';
import { theme } from 'styles';
import { DocumentCategory, UserStatus } from 'types';
import { getFullName } from 'utils';

import {
  BottomSheetHandle,
  CancelButton,
  Dialog,
  FooterActions,
  PrimaryButton,
  SearchField,
  SectionLabel,
  UserListBox,
  UserRow,
  VisibilityRow,
} from './styled';

interface ShareAccessModalProps {
  open: boolean;
  onClose: () => void;
  defaultView?: 'add' | 'manage';
  category?: DocumentCategory;
}

export function ShareAccessModal({
  open,
  onClose,
  defaultView = 'add',
  category,
}: ShareAccessModalProps) {
  const [view, setView] = useState<'add' | 'manage'>(defaultView);
  const [search, setSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [revokeUserId, setRevokeUserId] = useState<string | null>(null);
  const [isPublic, setIsPublic] = useState<boolean>(category?.visibility === 'public');

  const { updateCategoryAssigneesMutation } = useUpdateCategoryAssigneesMutation();
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const { usersList } = useUsersListQuery({
    status: [UserStatus.Active, UserStatus.Registered],
  });

  useEffect(() => {
    if (open) {
      setView(defaultView);
      setSearch('');
      setSelectedUserIds([]);
      setIsPublic(category?.visibility === 'public');
    }
  }, [open, defaultView, category?.visibility]);

  const accessUsers: CategoryAssignee[] = category?.assigned_to || [];

  const filteredUsers = usersList.filter((user) => {
    const name = getFullName({ firstName: user.firstName, lastName: user.lastName });
    const term = search.toLowerCase();
    return name.toLowerCase().includes(term) || user.email.toLowerCase().includes(term);
  });

  const filteredAccessUsers = accessUsers.filter((user) => {
    const name = `${user.first_name} ${user.last_name}`;
    const term = search.toLowerCase();
    return name.toLowerCase().includes(term) || user.email.toLowerCase().includes(term);
  });

  const handleClose = () => {
    setSearch('');
    setSelectedUserIds([]);
    onClose();
  };

  const handleApply = async () => {
    if (!category?.id || selectedUserIds.length === 0) return;
    const existing = accessUsers.map((u) => u.id);
    const newIds = selectedUserIds.filter((id) => !existing.includes(id));
    const assigned_to = [...existing, ...newIds];
    await updateCategoryAssigneesMutation({ categoryId: category.id, assigned_to });
    alert.success(t('resources.documentCategories.share.toasts.shared'));
    handleClose();
  };

  const handleRevoke = async () => {
    if (!category?.id || !revokeUserId) return;
    const assigned_to = accessUsers.filter((u) => u.id !== revokeUserId).map((u) => u.id);
    await updateCategoryAssigneesMutation({ categoryId: category.id, assigned_to });
    alert.success(t('resources.documentCategories.share.toasts.accessRevoked'));
    setRevokeUserId(null);
  };

  // const handleVisibilityToggle = async () => {
  //   if (!category?.id) return;
  //   const nextIsPublic = !isPublic;
  //   setIsPublic(nextIsPublic);
  //   if (nextIsPublic) {
  //     await updateCategoryAssigneesMutation({
  //       categoryId: category.id,
  //       visibility: 'public',
  //     });
  //   } else {
  //     await updateCategoryAssigneesMutation({
  //       categoryId: category.id,
  //       assigned_to: accessUsers.map((u) => u.id),
  //       visibility: 'private',
  //     });
  //   }
  // };

  const revokeUser = accessUsers.find((u) => u.id === revokeUserId);
  const revokeUserName = revokeUser ? `${revokeUser.first_name} ${revokeUser.last_name}` : '';

  const modalContent = (
    <>
      {!isPublic && (
        <SearchField
          fullWidth
          placeholder={t('resources.documentCategories.share.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
              </InputAdornment>
            ),
          }}
        />
      )}

      {view === 'manage' ? (
        <>
          {isPublic && (
            <VisibilityRow>
              <Box display="flex" alignItems="center" gap="10px">
                <Typography fontSize="12px" color="text.secondary">
                  {t('resources.documentCategories.share.publicDescription')}
                </Typography>
              </Box>
            </VisibilityRow>
          )}

          {!isPublic && (
            <>
              <SectionLabel>{t('resources.documentCategories.share.usersListTitle')}</SectionLabel>
              <UserListBox>
                {filteredAccessUsers.length === 0 ? (
                  <Box px={2} py={2}>
                    <Typography fontSize="14px" color="text.secondary">
                      {t('resources.documentCategories.share.emptyState')}
                    </Typography>
                  </Box>
                ) : (
                  filteredAccessUsers.map((user) => (
                    <UserRow key={user.id}>
                      <EmailIcon
                        fontSize="small"
                        sx={{ color: theme.palette.secondary.dark, flexShrink: 0 }}
                      />
                      <Box flex={1} minWidth={0}>
                        <Typography fontSize="14px" fontWeight={500} noWrap>
                          {user.first_name} {user.last_name}
                        </Typography>
                        <Typography fontSize="12px" color="text.secondary" noWrap>
                          {user.email}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={() => setRevokeUserId(user.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </UserRow>
                  ))
                )}
              </UserListBox>
            </>
          )}

          <FooterActions>
            <CancelButton
              variant="outlined"
              onClick={() => {
                if (view === 'manage') {
                  handleClose();
                } else {
                  setView('add');
                }
              }}
            >
              {view === 'manage'
                ? t('resources.documentCategories.share.buttons.cancel')
                : t('resources.documentCategories.share.buttons.goBack')}
            </CancelButton>
          </FooterActions>
        </>
      ) : (
        <>
          {isPublic ? (
            <VisibilityRow>
              <Box display="flex" alignItems="center" gap="10px">
                <Typography fontSize="12px" color="text.secondary">
                  {t('resources.documentCategories.share.publicDescription')}
                </Typography>
              </Box>
            </VisibilityRow>
          ) : (
            <UserListBox>
              {filteredUsers.map((user) => {
                const name = getFullName({ firstName: user.firstName, lastName: user.lastName });
                const hasAccess = accessUsers.some((u) => u.id === user.id);
                return (
                  <UserRow
                    key={user.id}
                    onClick={() => {
                      if (hasAccess) return;
                      setSelectedUserIds((prev) =>
                        prev.includes(user.id)
                          ? prev.filter((id) => id !== user.id)
                          : [...prev, user.id],
                      );
                    }}
                    sx={{ opacity: hasAccess ? 0.5 : 1, cursor: hasAccess ? 'default' : 'pointer' }}
                  >
                    <EmailIcon
                      fontSize="small"
                      sx={{ color: theme.palette.secondary.dark, flexShrink: 0 }}
                    />
                    <Box flex={1} minWidth={0}>
                      <Typography fontSize="14px" fontWeight={500} noWrap>
                        {name}
                      </Typography>
                      <Typography fontSize="12px" color="text.secondary" noWrap>
                        {user.email}
                      </Typography>
                    </Box>
                    <Checkbox
                      checked={hasAccess || selectedUserIds.includes(user.id)}
                      disabled={hasAccess}
                      size="small"
                      sx={{ p: 0, flexShrink: 0 }}
                      color="info"
                    />
                  </UserRow>
                );
              })}
            </UserListBox>
          )}
          <FooterActions>
            <CancelButton variant="outlined" onClick={handleClose}>
              {t('resources.documentCategories.share.buttons.cancel')}
            </CancelButton>
            <PrimaryButton
              variant="contained"
              onClick={handleApply}
              disabled={selectedUserIds.length === 0}
            >
              {t('resources.documentCategories.share.buttons.apply')}
            </PrimaryButton>
          </FooterActions>
        </>
      )}
    </>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              borderRadius: '20px 20px 0 0',
              maxHeight: '90dvh',
              display: 'flex',
              flexDirection: 'column',
              px: 2,
              pt: 0,
              overflowY: 'auto',
              paddingBottom: '16px',
            },
          }}
        >
          <BottomSheetHandle />
          {modalContent}
        </Drawer>
      ) : (
        <Dialog open={open} onClose={handleClose}>
          {modalContent}
        </Dialog>
      )}

      <NewConfirmDialog
        open={!!revokeUserId}
        title={t('resources.documentCategories.revokeAccessModal.title', {
          userName: revokeUserName,
        })}
        subTitle={t('resources.documentCategories.revokeAccessModal.subtitle')}
        onConfirm={handleRevoke}
        onClose={() => setRevokeUserId(null)}
        submitLabel={t('resources.documentCategories.revokeAccessModal.buttons.revoke')}
        cancelLabel={t('resources.documentCategories.revokeAccessModal.buttons.cancel')}
      />
    </>
  );
}
